// Parent Homework "Today" screen (SC-H1a).
// Pure render from already-fetched data (homeworks + compMap). No fetch, no re().
// Slot is the unit of completion (D13): one tile per (exercise, slot); counts iterate
// exerciseSlotsOn and use the same key format as the legacy day strip so counts agree.

import { isExerciseScheduledOn, exerciseSlotsOn } from '../data.js';
import { _findNextAppointment } from '../parent-view.js';
import { openReminderSheet, hwHasReminders } from './hw-reminders.js';
import { printHomeworkSheet } from './print-sheet.js';

const T = (k, p) => window.HUD?.T?.(k, p) || k;
const _pad = n => String(n).padStart(2, '0');
const _fmtLocal = d => d.getFullYear() + '-' + _pad(d.getMonth() + 1) + '-' + _pad(d.getDate());
const _locDate = (d, o) => window.HUD?._locDate ? window.HUD._locDate(d, o, []) : d.toLocaleDateString(undefined, o);

const SLOT_ORDER = ['morning', 'afternoon', 'anytime', 'evening']; // §2.1: Anytime before Evening
const KNOWN_SLOTS = new Set(['morning', 'afternoon', 'evening']);

// Deterministic per-exercise tint + art (design §2.4 "none" case). Stable by exercise id.
const TINTS = ['sky', 'lav', 'mint', 'amber'];
const ART = {
  sky: ['🪶', '💧', '🫧', '🌬️'],
  lav: ['🎵', '🐝', '🎈', '🗣️'],
  mint: ['📖', '🌿', '🧩', '⭐'],
  amber: ['✂️', '🧵', '🖍️', '🎯'],
};
const DOT_VARS = ['var(--teal)', 'var(--lav-d)', 'var(--sky)', 'var(--amber)', 'var(--coral)', 'var(--rose)'];
function _hash(s) { let h = 0; const str = String(s || ''); for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0; return h; }
function _tintFor(exId) { return TINTS[_hash(exId) % TINTS.length]; }
function _artFor(exId) { const t = _tintFor(exId); const set = ART[t]; return set[_hash(exId) % set.length]; }
function _dotColor(specId) { return DOT_VARS[_hash(specId) % DOT_VARS.length]; }
function _firstName(name) { return String(name || 'Specialist').trim().split(/\s+/)[0]; }

const VIDEO_RE = /\.(mp4|mov|webm|m4v|avi|mkv)$/i;
const IMAGE_RE = /\.(png|jpe?g|gif|webp|heic|heif)$/i;
function _firstMedia(ex) {
  const names = ex.attached_file_names || [], paths = ex.attached_file_paths || [], urls = ex.attached_file_urls || [];
  const n = Math.max(names.length, paths.length, urls.length);
  for (let i = 0; i < n; i++) {
    const nm = names[i] || '';
    if (VIDEO_RE.test(nm)) return { kind: 'video', path: paths[i] || null, url: urls[i] || null };
    if (IMAGE_RE.test(nm)) return { kind: 'image', path: paths[i] || null, url: urls[i] || null };
  }
  return { kind: 'none' };
}

// Dose string. Reuses hw3_ dose keys for the default "reps" unit; SC-H3 adds sets×duration
// ("4×20 sec") and measure_unit ("2 sheets") so timer/counter doses read correctly.
function _dose(ex) {
  if (ex.reps) {
    const unit = ex.measure_unit || null;
    if (unit) return (ex.sets > 1 ? ex.sets + '×' + ex.reps : String(ex.reps)) + ' ' + unit;
    return ex.sets > 1 ? T('hw3_sets_reps', { sets: ex.sets, reps: ex.reps }) : T('hw3_reps', { reps: ex.reps });
  }
  if (ex.duration_seconds) {
    const m = Math.round(ex.duration_seconds / 60);
    const per = m >= 1 ? T('hw3_min', { n: m }) : T('hw3_sec', { n: ex.duration_seconds });
    return ex.sets > 1 ? ex.sets + '×' + per : per;
  }
  return T('hw3_one_time');
}

// Recurrence label for the "Not today" line — mirrors parent-view.js bubble meta.
function _recurrenceLabel(hw) {
  const rec = hw.recurrence || 'daily';
  if (rec === 'daily') return T('hw4_daily');
  if (rec === 'specific_days') return (hw.specific_days || []).map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(' · ');
  if (rec === 'every_other_day') return T('hw3_every_other_day');
  if (rec === 'once') return T('hw3_one_time');
  return rec;
}

const _slotName = s => KNOWN_SLOTS.has(s) ? T('hw5_' + s) : T('hw5_anytime');
const _slotBucket = s => (KNOWN_SLOTS.has(s) ? s : 'anytime');
const _doneKey = (exId, dStr, slot) => exId + ':' + dStr + ':' + (slot || '');
const _isSlotDone = (compMap, exId, dStr, slot) => compMap[_doneKey(exId, dStr, slot)]?.status === 'done';
const _compFor = (compMap, exId, dStr, slot) => compMap[_doneKey(exId, dStr, slot)];

// Every (hw, ex, slot) scheduled on `date`. hw must be non-paused + active (loadHomeworkForParent
// already filters status='active'; is_paused guarded here to match the bubble list).
function _slotItemsOn(homeworks, date) {
  const out = [];
  (homeworks || []).forEach(hw => {
    if (hw.is_paused) return;
    (hw.exercises || []).forEach(ex => {
      if (!isExerciseScheduledOn(hw, ex, date)) return;
      exerciseSlotsOn(hw, ex, date).forEach(slot => out.push({ hw, ex, slot }));
    });
  });
  return out;
}

function _countOn(homeworks, compMap, date) {
  const dStr = _fmtLocal(date);
  let total = 0, done = 0;
  _slotItemsOn(homeworks, date).forEach(({ ex, slot }) => { total++; if (_isSlotDone(compMap, ex.id, dStr, slot)) done++; });
  return { total, done };
}

// Slot-major grouping (SC-H4.1). ONE helper shared by the Today/board tiles AND the exercise
// queue so the walk order can never drift from the visual grouping. Buckets `items` (from
// _slotItemsOn) by _slotBucket, returns [{ bucket, items }] in SLOT_ORDER (skips empty buckets).
function _slotGroups(items) {
  const buckets = {};
  (items || []).forEach(x => { const b = _slotBucket(x.slot); (buckets[b] = buckets[b] || []).push(x); });
  return SLOT_ORDER.filter(b => buckets[b] && buckets[b].length).map(b => ({ bucket: b, items: buckets[b] }));
}

function _countRange(homeworks, compMap, from, to) {
  let total = 0, done = 0;
  const cur = new Date(from); cur.setHours(0, 0, 0, 0);
  const end = new Date(to); end.setHours(0, 0, 0, 0);
  while (cur <= end) { const c = _countOn(homeworks, compMap, cur); total += c.total; done += c.done; cur.setDate(cur.getDate() + 1); }
  return { total, done };
}

export function todaySlotTotal(homeworks, compMap) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return _countOn(homeworks, compMap, today);
}

// Shared tab row (Today | All homework). active = 'today' | 'all'.
export function buildTabRow(active, todayCount, allCount, onToday, onAll) {
  const row = document.createElement('div');
  row.className = 'hwp-tabs';
  row.innerHTML =
    `<button class="hwp-tab ${active === 'today' ? 'on' : ''}" data-t="today">${T('hw5_today')} <span class="n">${todayCount}</span></button>` +
    `<button class="hwp-tab ${active === 'all' ? 'on' : ''}" data-t="all">${T('hw5_all_homework')} <span class="n">${allCount}</span></button>`;
  row.querySelector('[data-t="today"]').onclick = () => { if (active !== 'today') onToday(); };
  row.querySelector('[data-t="all"]').onclick = () => { if (active !== 'all') onAll(); };
  return row;
}

// Tile HTML for one (hw, ex, slot). Explicit params (SC-H1b: reused by Today and the board).
// withFrom adds the "<homework> · <specialist>" line (Today only; the board omits it).
export function tileHtml(hw, ex, slot, compMap, dStr, multiSlot, withFrom) {
  const c = _compFor(compMap, ex.id, dStr, slot);
  const done = c?.status === 'done';
  const off = !!c && !done;
  const tint = _tintFor(ex.id);
  const media = _firstMedia(ex);
  const art = _artFor(ex.id);
  const stateGlyph = done ? '✓' : off ? (c.status === 'skipped' ? '—' : '✕') : '›';
  const thumbInner = media.kind === 'video' ? (art + '<span class="play">▶</span>') : art;
  const dose = _dose(ex) + (multiSlot ? ' · ' + _slotName(slot) : '');
  const from = withFrom
    ? `<p class="hwp-from"><i class="hwp-dot" style="background:${_dotColor(hw.specialist_id)}"></i>${_esc(hw.title)} · ${_esc(_firstName(hw.specialist_name))}</p>`
    : '';
  // SC3.1: quiet per-homework reminder bell (Today only — withFrom). Teal when reminders are set,
  // grey when not. Tap opens the same slot panel as a bottom sheet (wired in wireTiles).
  const bell = withFrom
    ? `<button class="hwp-tile-bell${hwHasReminders(hw.id) ? ' on' : ''}" data-hw="${hw.id}" aria-label="${_esc(T('hw5_remind_me'))}">🔔</button>`
    : '';
  return `<div class="hwp-tile ${done ? 'done' : ''} ${off ? 'off' : ''}" data-hw="${hw.id}" data-ex="${ex.id}" data-slot="${slot}" data-thumbpath="${media.kind === 'image' && media.path ? _esc(media.path) : ''}">
    <div class="hwp-thumb t-${tint}">${thumbInner}</div>
    <div class="hwp-body"><h3 class="ff">${_esc(ex.title || T('hw3_exercise'))}</h3><p class="hwp-dose">${dose}</p>${from}</div>
    ${bell}<div class="hwp-state">${stateGlyph}</div>${done ? '<span class="hwp-stk">🌟</span>' : ''}</div>`;
}

// Attach tile tap + lazy thumbnail signing to every real (non-na) tile inside `wrap`.
// Shared by Today and the board (SC-H1b).
export function wireTiles(wrap, onTileTap, sign, homeworks) {
  wrap.querySelectorAll('.hwp-tile[data-ex]:not(.na)').forEach(t => {
    t.onclick = () => onTileTap(t.dataset.hw, t.dataset.ex, t.dataset.slot);
    const p = t.dataset.thumbpath;
    if (p) {
      const thumb = t.querySelector('.hwp-thumb');
      sign(p).then(url => { if (url && thumb) { thumb.style.backgroundImage = `url("${url}")`; thumb.textContent = ''; } });
    }
  });
  // SC3.1: reminder bells (Today only). Tap → same slot panel as a bottom sheet; on save, retint every
  // bell for that homework in place. stopPropagation so the tile's exercise tap never fires.
  if (homeworks) {
    wrap.querySelectorAll('.hwp-tile-bell[data-hw]').forEach(b => {
      b.onclick = e => {
        e.stopPropagation();
        const hw = homeworks.find(h => h.id === b.dataset.hw);
        if (!hw) return;
        openReminderSheet(hw, window.HUD, () => {
          const on = hwHasReminders(hw.id);
          wrap.querySelectorAll(`.hwp-tile-bell[data-hw="${hw.id}"]`).forEach(x => x.classList.toggle('on', on));
        });
      };
    });
  }
}

function _esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])); }

// Next scheduled weekday for a homework not due today (scan forward up to 14 days).
function _nextWeekday(hw) {
  const cur = new Date(); cur.setHours(0, 0, 0, 0);
  for (let i = 1; i <= 14; i++) {
    cur.setDate(cur.getDate() + 1);
    if ((hw.exercises || []).some(ex => isExerciseScheduledOn(hw, ex, cur))) return _locDate(cur, { weekday: 'long' });
  }
  return null;
}

// ctx: { homeworks, compMap, childId, isWeb, H, allCount, onTileTap, onGoAll, sign }
export function renderTodayInto(wrap, ctx) {
  const { homeworks, compMap, H } = ctx;
  const el = H.el;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayStr = _fmtLocal(today);
  const child = H.DB?.children?.find(c => c.id === ctx.childId);

  const items = _slotItemsOn(homeworks, today);
  const total = items.length;
  const done = items.filter(x => _isSlotDone(compMap, x.ex.id, todayStr, x.slot)).length;

  // Follow line (D10): yesterday + week-to-date (Sunday-first, Israel).
  const yday = new Date(today); yday.setDate(yday.getDate() - 1);
  const y = _countOn(homeworks, compMap, yday);
  const weekStart = new Date(today); weekStart.setDate(weekStart.getDate() - today.getDay()); // Sunday
  const wk = _countRange(homeworks, compMap, weekStart, today);

  // Tomorrow's slot count for the all-done card.
  const tmr = new Date(today); tmr.setDate(tmr.getDate() + 1);
  const tmrCount = _countOn(homeworks, compMap, tmr).total;

  const off = 150.8 * (1 - (total ? done / total : 0));
  const childLine = ((child?.avatar || '') + ' ' + (child?.name || '')).trim() + ' · ' + _locDate(today, { weekday: 'long', month: 'short', day: 'numeric' });

  const container = document.createElement('div');
  container.innerHTML =
    `<div class="hwp-titlerow"><div class="hwp-ring"><svg viewBox="0 0 56 56" width="56" height="56"><circle class="c" cx="28" cy="28" r="24"/><circle class="p" cx="28" cy="28" r="24" stroke-dasharray="150.8" stroke-dashoffset="${off}"/></svg><b>${done}/${total}</b></div>` +
    `<div><h1 class="ff">${T('hw5_today')}</h1><p>${_esc(childLine)}</p></div></div>` +
    `<p class="hwp-yday">${T('hw5_follow_line', { a: y.done, b: y.total, c: wk.done, d: wk.total })}</p>`;
  // Track B (SC4c) — labeled print pill opposite the Today title (same chip component as the weekly
  // sheet). Prints a DAILY sheet (today's exercises + today's slots). Shown only when something is
  // scheduled today. margin-inline-start:auto puts it at the row's inline-END (mirrors in RTL).
  if (total > 0) {
    const pb = document.createElement('button');
    pb.className = 'hwp-print-chip';
    pb.title = T('hwp_print_today');
    pb.innerHTML = '🖨 <span>' + _esc(T('hwp_print_today')) + '</span>';
    pb.onclick = () => printHomeworkSheet(homeworks, ctx.childId, H, 'day');
    container.querySelector('.hwp-titlerow')?.appendChild(pb);
  }
  wrap.appendChild(container);

  // Tab row (Today active)
  wrap.appendChild(buildTabRow('today', total, ctx.allCount, () => {}, ctx.onGoAll));

  // Everything-done card
  if (total > 0 && done === total) {
    const ad = document.createElement('div');
    ad.className = 'hwp-alldone';
    ad.textContent = '🌟 ' + T('hw5_all_done_today', { n: tmrCount });
    wrap.appendChild(ad);
  }

  // Slot groups (slot-major, shared helper)
  const multiSlotFor = {};
  homeworks.forEach(hw => (hw.exercises || []).forEach(ex => { multiSlotFor[ex.id] = exerciseSlotsOn(hw, ex, today).length > 1; }));

  _slotGroups(items).forEach(({ items: arr }) => {
    const gdone = arr.filter(x => _isSlotDone(compMap, x.ex.id, todayStr, x.slot)).length;
    const gh = document.createElement('div');
    gh.className = 'hwp-grp';
    gh.textContent = _slotName(arr[0].slot) + ' · ' + gdone + '/' + arr.length;
    wrap.appendChild(gh);
    const holder = document.createElement('div');
    holder.innerHTML = arr.map(x => tileHtml(x.hw, x.ex, x.slot, compMap, todayStr, multiSlotFor[x.ex.id], true)).join('');
    while (holder.firstChild) wrap.appendChild(holder.firstChild);
  });

  // Not today group — active homeworks with nothing scheduled today
  const notToday = homeworks.filter(hw => !hw.is_paused && !(hw.exercises || []).some(ex => isExerciseScheduledOn(hw, ex, today)));
  if (notToday.length) {
    const gh = document.createElement('div'); gh.className = 'hwp-grp'; gh.textContent = T('hw5_not_today'); wrap.appendChild(gh);
    notToday.forEach(hw => {
      const wd = _nextWeekday(hw);
      const line = _recurrenceLabel(hw) + (wd ? ' · ' + T('hw5_next_on', { weekday: wd }) : '');
      const art = _artFor((hw.exercises || [])[0]?.id || hw.id);
      const t = document.createElement('div');
      t.className = 'hwp-tile na';
      t.innerHTML = `<div class="hwp-thumb" style="background-color:var(--mint-ll)">${art}</div>` +
        `<div class="hwp-body"><h3 class="ff">${_esc(hw.title)}</h3><p class="hwp-dose">${_esc(line)}</p>` +
        `<p class="hwp-from"><i class="hwp-dot" style="background:${_dotColor(hw.specialist_id)}"></i>${_esc(_firstName(hw.specialist_name))}</p></div>`;
      wrap.appendChild(t);
    });
  }

  // Wire tile taps + async image thumbnails (pooled signer) + reminder bells (Today only)
  wireTiles(wrap, ctx.onTileTap, ctx.sign, homeworks);
}

// Shared helpers reused by the All-homework list and the per-homework board (SC-H1b).
export { _fmtLocal, _locDate, SLOT_ORDER, _tintFor, _artFor, _dotColor, _firstName, _dose, _recurrenceLabel, _slotName, _slotBucket, _isSlotDone, _slotItemsOn, _slotGroups, _countOn, _esc, _nextWeekday };
